import { WarningItem } from "@services/ErrorTypes";
import { Country } from "@utils/constants/localisation";
import { Payload } from "@utils/http/AxiosAPI";
import { IMSTokenParams, getWithIDToken, postWithIdToken } from "@utils/http/MSAuthAPI";
import { PlanPayload } from "@views/journeys/OnboardingFormPayloadFactory";

interface IObtainSignUrl {
    sign_url: string
};

interface IObtainCustomerOverviewUrl {
    url: string
};


export interface IRedisKey {
    make: string
    model: string
    year: string
    licencePlate: string
    fleetioLink: string
    zohoLink: string
    chargebeeSubsLink?: string
    plan: string;
    email: string
    missingKey?: boolean
    isFoV2?: boolean
};

type RedisResponse = {
    data: IRedisKey;
    warnings?: WarningItem[];
}


interface IOnboardingService {
    obtainPotentialRedirectURL(potential_id: string, country: Country, IDTokenParams: IMSTokenParams): Promise<IObtainCustomerOverviewUrl>;
    obtainCustomerSignUrl(payload: Payload | PlanPayload, country: Country, IDTokenParams: IMSTokenParams): Promise<IObtainSignUrl>;
    obtainCSRSignUrl(payload: Payload, country: Country, IDTokenParams: IMSTokenParams): Promise<IObtainSignUrl>;
    postRedisKey(key: string, country: Country, IDTokenParams: IMSTokenParams): Promise<RedisResponse>;
    createSubscription(customerId: string, key: string, country: Country, IDTokenParams: IMSTokenParams): Promise<RedisResponse>;
};


export const OnboardingService: IOnboardingService = {

    async obtainPotentialRedirectURL(potentialID: string, country: Country, IDTokenParams: IMSTokenParams): Promise<IObtainCustomerOverviewUrl> {
        const resp = await getWithIDToken(
            `v1/potentials/${potentialID}/contact_url?country=${country}`,
            IDTokenParams,
            { country: country, potential_id: potentialID }
        );
        return resp.data;
    },

    async obtainCustomerSignUrl(payload: Payload | PlanPayload, country: Country, IDTokenParams: IMSTokenParams): Promise<IObtainSignUrl> {
        const resp = await postWithIdToken(
            'v1/signatures/obtain-customer',
            IDTokenParams,
            { country },
            payload
        );
        return resp.data;
    },

    async obtainCSRSignUrl(payload: Payload, country: Country, IDTokenParams: IMSTokenParams): Promise<IObtainSignUrl> {
        const resp = await postWithIdToken(
            'v1/signatures/obtain-csr',
            IDTokenParams,
            { country },
            payload
        );
        return resp.data;
    },

    async postRedisKey(key: string, country: Country, IDTokenParams: IMSTokenParams): Promise<RedisResponse> {
        const resp = await postWithIdToken(
            'v1/signatures/process',
            IDTokenParams,
            { country, key },
        );

        const rawPayload = resp.data?.data;
        const rawWarnings = resp.data?.warnings ?? [];

        const processedData = {
            year: rawPayload?.year,
            make: rawPayload?.make,
            model: rawPayload?.model,
            licencePlate: rawPayload?.license_plate,
            vehicle: rawPayload?.vehicle,
            fleetioLink: rawPayload?.fleetio_url,
            chargebeeSubsLink: rawPayload?.chargebee_subscription_url,
            zohoLink: rawPayload?.zoho_url,
            email: rawPayload?.email,
            plan: rawPayload?.plan,
            missingKey: rawPayload?.missing_key,
            isFoV2: rawPayload?.is_fo_v2
        };

        return {
            data: processedData,
            warnings: rawWarnings
        };
    },

    // Create subscription for FO 2.0
    async createSubscription(customerId: string, key: string, country: Country, IDTokenParams: IMSTokenParams): Promise<RedisResponse> {
        const resp = await postWithIdToken(
            `v1/customers/${customerId}/subscription`,
            IDTokenParams,
            { country, key },
        );

        const rawPayload = resp.data?.data;
        const rawWarnings = resp.data?.warnings ?? [];

        const processedData = {
            year: rawPayload?.year,
            make: rawPayload?.make,
            model: rawPayload?.model,
            licencePlate: rawPayload?.license_plate,
            vehicle: rawPayload?.vehicle,
            fleetioLink: rawPayload?.fleetio_url,
            chargebeeSubsLink: rawPayload?.chargebee_subscription_url,
            zohoLink: rawPayload?.zoho_url,
            email: rawPayload?.email,
            plan: rawPayload?.plan,
            missingKey: rawPayload?.missing_key,
            isFoV2: true  // Always true for v2
        };

        return {
            data: processedData,
            warnings: rawWarnings
        };
    }

};
